/* eslint-disable no-shadow */
import React, { useCallback, useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { Card, CardContent, CardHeader, CardTitle } from '@mesh.ai/rabbit';
import { GOALS_OVERVIEW_PAGE_METRIC_CARDS } from 'Deer/metricCards/constants';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { showOKRDetails } from 'components/RabbitOKRSidePanel/okrPanelControlSlice';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { OwnersAndCollaborators } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/OwnersAndCollaborators';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

import { TableCell } from './TableCell';

const GoalsTable = ({ data, gridRef, setCardKPIData, refetchCascadedViewData }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { keyResultsName } = useOrganisationSettings();

  const columnDefs = useMemo(
    () => [
      {
        field: 'toIds',
        width: 100,
        filter: true,
        sortable: true,
        headerName: 'Owner',
        // will be used for searching by owner and employee function
        valueGetter: (data) => data.data?.toIds?.map(({ displayName }) => `${displayName}`),
        cellRenderer: OwnersAndCollaborators,
      },
    ],
    []
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: 'Objectives',
      minWidth: 300,
      flex: 1,
      filter: true,
      cellStyle: { height: 'auto' },
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: TableCell,
      },
      valueGetter: (data) => data.data ?? {},
    }),
    []
  );
  const getDataPath = useMemo(() => (data) => data.hierarchy, []);

  const rowClickHandler = ({ data }) => {
    const { type, uuid, unMasked } = data ?? {};
    if (!unMasked) {
      return;
    }
    dispatch(
      // @ts-expect-error error (^.^)
      showOKRDetails({
        id: uuid,
        type,
        onDelete: refetchCascadedViewData,
        onUpdate: refetchCascadedViewData,
        history,
      })
    );
  };

  const getRowId = useCallback(({ data }) => data?.uuid, []);

  const getRowStyle = (params) => {
    if (params?.data?.unMasked) {
      return { cursor: 'pointer' };
    }
    return null;
  };

  const handleKeyResultValuesUpdated = useCallback(() => {
    const newRowData = [];
    gridRef?.current?.api?.forEachNodeAfterFilterAndSort((row) => {
      newRowData.push(row?.data);
    });
    const totalKeyResults = newRowData.filter((row) => row.type === OKR_TYPES.KEY_RESULT);
    const keyResultsWithNoCheckIns = totalKeyResults.filter((row) => !row.lastCheckIn);
    const keyResultsAtRisk = totalKeyResults.filter((row) => row.progressLevel === 'AT_RISK');
    const sumOfAllProgresses = totalKeyResults.reduce((prev, cur) => (cur?.avgProgressPercentage || 0) + prev, 0);
    const avgProgressPercentage = sumOfAllProgresses / (totalKeyResults.length || 1);

    setCardKPIData([
      {
        title: `${modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)} count`,
        value: totalKeyResults.length,
      },
      {
        title: `${modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)} with no check-ins`,
        value: keyResultsWithNoCheckIns.length,
      },
      {
        cardId: GOALS_OVERVIEW_PAGE_METRIC_CARDS.myData.goalsAtRisk,
        title: `${modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)} at-risk`,
        value: keyResultsAtRisk.length,
      },
      {
        title: `Average progress`,
        value: `${avgProgressPercentage?.toFixed(2) || 0}%`,
      },
    ]);
  }, [keyResultsName, gridRef, setCardKPIData]);

  return (
    <Card style={{ height: '100%' }}>
      <CardHeader>
        <CardTitle>List</CardTitle>
      </CardHeader>
      <CardContent>
        <Box
          sx={{
            height: '420px',
            overflow: 'auto',
          }}
        >
          <AgGridTable
            ref={gridRef}
            rowData={data}
            columnDefs={columnDefs}
            autoGroupColumnDef={autoGroupColumnDef}
            treeData
            animateRows={false}
            // groupDefaultExpanded={-1}
            getDataPath={getDataPath}
            domLayout="autoHeight"
            onCellClicked={rowClickHandler}
            getRowId={getRowId}
            getRowStyle={getRowStyle}
            headerHeight={0}
            rowHeight={100}
            containerAutoHeight
            onModelUpdated={() => {
              handleKeyResultValuesUpdated();
            }}
            isOverviewPage
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export { GoalsTable };
